import * as React from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import Icon from 'components/Icon';
import { userSession } from 'state/session';
import { showToast } from 'state/toast';
import { analyticsState } from 'state/analytics';
import { QrCodeComponent } from 'components/QrCodeComponent';
import { copyQrCodeAsImage } from 'utils/ui/copyQrCodeAsImage';
import { writeTextToClipboard } from 'utils/clipboard';

export interface IDgaEndModalProps {
  onModalClose?: () => void;
}

const DgaEndModal: React.FC<IDgaEndModalProps> = ({ onModalClose }) => {
  const session = userSession().useState();
  const { t } = useLingui();
  const dgaLink = (session.dgaLink ?? '') as string;

  const qrRef = React.useRef<HTMLDivElement | null>(null);
  const copyLinkToClipboard = () => {
    writeTextToClipboard(dgaLink).then(
      () => {
        showToast({ title: t`Link copied to clipboard`, type: 'success', notificationId: 'COPY_LINK_SUCCESS' });
      },
      (err) => {
        console.error('Could not copy URL: ', err);
        showToast({
          title: t`Could not copy URL`,
          message: err.message,
          type: 'error',
          notificationId: 'COPY_LINK_ERROR',
        });
      }
    );
    try {
      analyticsState().setValues({ dealerToolUsed: 'copyDGAUrl' });
    } catch (error) {
      console.error('Analytics error', error);
    }
  };

  const openDga = (e: React.MouseEvent) => {
    e.preventDefault();
    const newWindow = window.open(dgaLink, '_blank');

    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      // Fallback: navigate directly if popup blocked
      window.location.href = dgaLink;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <div className="pointer-events-auto relative w-auto max-w-[570px] rounded-xl bg-white px-6 py-5 text-gray-900 shadow-lg">
        {/* Close button */}
        <div className="flex shrink-0 items-end justify-end">
          <button
            type="button"
            onClick={onModalClose}
            className="inline-flex rounded-md text-gray-900 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:ring-offset-2"
          >
            <span className="sr-only">
              <Trans>Close</Trans>
            </span>
            <Icon aria-hidden="true" className="ex3d-icon--close size-5 bg-teal" />
          </button>
        </div>

        {/* Title & Description */}
        <h2 className="mb-1 text-lg font-semibold">
          <Trans>Your session has ended</Trans>
        </h2>
        <p className="mb-5 text-sm text-gray-700">
          <Trans>
            The DGA might take several minutes to load. However, you can already share it with your customer.
          </Trans>
        </p>

        {/* QR + Actions */}
        <div className="mt-6 flex items-end justify-between">
          {/* QR Code - aligned left */}
          <div ref={qrRef} className="shrink-0">
            <QrCodeComponent value={dgaLink} size={75} />
          </div>

          {/* Buttons - aligned right in a row */}
          <div className="flex gap-4 text-sm">
            <button
              onClick={() => copyQrCodeAsImage(qrRef as unknown as React.RefObject<HTMLDivElement>)}
              className="flex items-center text-teal-700 hover:underline focus:outline-none"
            >
              <Trans>Copy QR Code</Trans>
              <Icon className="ex3d-icon--copy ml-1 size-4 bg-teal" />
            </button>

            <button
              onClick={copyLinkToClipboard}
              className="flex items-center text-teal-700 hover:underline focus:outline-none"
            >
              <Trans>Copy link</Trans>
              <Icon className="ex3d-icon--copy ml-1 size-4 bg-teal" />
            </button>

            <button
              onClick={openDga}
              className="flex items-center gap-1 rounded-full bg-teal px-4 py-2 text-sm text-white transition hover:opacity-90"
            >
              <Trans>Open DGA</Trans>
              <Icon className="ex3d-icon--external-link size-4 bg-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DgaEndModal;
